import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { useConfirm } from '@/components/ui/ConfirmProvider';
import { activeRecordings, activeUnsafeSends, subscribeRecordings } from '@/lib/recordingGuard';
import { subscribeAnswers, unsavedAnswers } from '@/lib/lessonAnswerGuard';

/**
 * Предупредить, что запись голоса пропадёт, если уйти со страницы (задача 6, 11.09.2026).
 *
 * ⚠️ ДВА ВИДА УХОДА — ДВА ПЕРЕХВАТА:
 * - внутри приложения (меню, «Назад», ссылка) — `useBlocker` и наше окно;
 * - закрытие вкладки и перезагрузка — `beforeunload`: текст там показывает
 *   браузер, свой подставить нельзя.
 *
 * Ставится ОДИН раз на страницу урока: `useBlocker` разрешает один перехват.
 *
 * ⚠️ ДВЕ ПРИЧИНЫ СПРАШИВАТЬ, А НЕ ОДНА (12.09.2026). Кроме идущей записи, есть
 * идущая ОТПРАВКА, которую уход теряет, — это домашка, где загрузка идёт мимо
 * очереди. Расширен именно этот хук, а не заведён второй: `useBlocker`
 * разрешает один перехват на страницу, и второй молча не сработал бы.
 *
 * ⚠️ ОТПРАВКА ИЗ УРОКА СЮДА НЕ ПОПАДАЕТ НАМЕРЕННО: там запись уже лежит в
 * очереди и уезжает сама, даже если закрыть вкладку. Спрашивать «точно уйти?»
 * значило бы пугать бедой, которой нет, — и приучать жать «уйти» не глядя.
 */
export function useLeaveWhileRecording(): void {
  const { t } = useTranslation();
  const ask = useConfirm();
  const recording = useSyncExternalStore(subscribeRecordings, activeRecordings, activeRecordings) > 0;
  const sending = useSyncExternalStore(subscribeRecordings, activeUnsafeSends, activeUnsafeSends) > 0;
  const risky = recording || sending;
  // ⚠️ ТРЕТЬЯ ПРИЧИНА (24.09.2026, сборка 61): начатый, но не проверенный ответ задания. Только в приложении —
  // там появился свайп назад от края, и уйти из урока случайно стало легко (решение владельца). На сайте
  // счётчик всегда 0: отметки ставит `lib/lessonAnswerGuard.ts` лишь под Capacitor. Закрытие вкладки
  // (`beforeunload` ниже) по этой причине не спрашиваем — в приложении вкладок нет.
  const unsaved = useSyncExternalStore(subscribeAnswers, unsavedAnswers, unsavedAnswers) > 0;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (risky || unsaved) && currentLocation.pathname !== nextLocation.pathname,
  );
  // Окно спрашивается один раз на одну попытку ухода: объект перехвата меняется
  // при каждой отрисовке, и без отметки окно открывалось бы заново.
  const asking = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked' || asking.current) return;
    asking.current = true;
    void ask(
      recording || sending
        ? {
            // Слова разные: недописанную запись можно дописать, а прерванную отправку
            // только начать заново. Одно сообщение на оба случая соврало бы про одно.
            message: recording
              ? t('blockRenderers.leaveWhileRecording')
              : t('blockRenderers.leaveWhileSending'),
            confirmLabel: recording
              ? t('blockRenderers.leaveWhileRecordingConfirm')
              : t('blockRenderers.leaveWhileSendingConfirm'),
            danger: true,
          }
        : {
            title: t('blockRenderers.leaveUnsavedTitle'),
            message: t('blockRenderers.leaveUnsaved'),
            confirmLabel: t('blockRenderers.leaveUnsavedConfirm'),
            cancelLabel: t('blockRenderers.leaveUnsavedStay'),
            danger: true,
          },
    ).then((leave) => {
      asking.current = false;
      if (leave) blocker.proceed?.();
      else blocker.reset?.();
    });
  }, [blocker, ask, t, recording, sending]);

  useEffect(() => {
    if (!risky) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Старым браузерам нужно непустое значение, чтобы показать окно.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [risky]);
}
